import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload,
    X,
    Plus,
    ArrowRight,
    ArrowLeft,
    Check,
    Loader2,
    FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
    getProfile,
    updateProfile,
    uploadResume,
    searchOrganizations,
    searchSchools,
} from '../api/insights';
import MonthYearPicker from './MonthYearPicker';
const PROFILE_SETUP_REQUIRED_KEY = 'insights_profile_setup_required';
const getProfileSetupRequiredKey = (userLike) =>
    `${PROFILE_SETUP_REQUIRED_KEY}:${String(userLike?.id || userLike?.email || 'anonymous')}`;
/* ─── helpers ── */
const emptyExperience = () => ({
    company: '',
    role: '',
    start_date: '',
    end_date: '',
    currently_working: false,
});
const emptyEducation = () => ({
    college: '',
    degree: '',
    start_date: '',
    end_date: '',
    currently_studying: false,
});
const MAX_RESUME_SIZE_BYTES = 300 * 1024;
const MIN_MONTH = '1960-01';
const CURRENT_YEAR_END_MONTH = `${new Date().getFullYear()}-12`;
const isValidMonth = (value) => /^\d{4}-\d{2}$/.test(value || '');
const clampToCurrentYearEnd = (value) => {
    if (!isValidMonth(value)) return '';
    return value > CURRENT_YEAR_END_MONTH ? CURRENT_YEAR_END_MONTH : value;
};
const validateProfileDates = (items, label, currentField) => {
    for (let i = 0; i < items.length; i += 1) {
        const row = items[i] || {};
        const start = row.start_date || '';
        const end = row.end_date || '';
        const isCurrent = !!row[currentField];
        if (start && (!isValidMonth(start) || start < MIN_MONTH || start > CURRENT_YEAR_END_MONTH)) {
            return `${label} ${i + 1}: start date must be between ${MIN_MONTH} and ${CURRENT_YEAR_END_MONTH}.`;
        }
        if (end && (!isValidMonth(end) || end < MIN_MONTH || end > CURRENT_YEAR_END_MONTH)) {
            return `${label} ${i + 1}: end date cannot be after ${CURRENT_YEAR_END_MONTH}.`;
        }
        if (!isCurrent && start && end && end < start) {
            return `${label} ${i + 1}: end date cannot be before start date.`;
        }
    }
    return null;
};
const toMonthValue = (value) => {
    const s = (value || '').toString().trim();
    if (!s) return '';
    if (/^\d{4}-\d{2}$/.test(s)) return s;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.slice(0, 7);
    return '';
};
/* ─── step meta ─── */
const STEPS = [
    { id: 1, label: 'Basic Info', icon: '👤', progress: 20 },
    { id: 2, label: 'Experience', icon: '💼', progress: 40 },
    { id: 3, label: 'Education', icon: '🎓', progress: 60 },
    { id: 4, label: 'Skills', icon: '⚡', progress: 80 },
    { id: 5, label: 'Open to Roles', icon: '🎯', progress: 100 },
];
/* ═══════════════════════════════════════════════════════════════ */
export default function ProfileSetup() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading: authLoading, openLogin } = useAuth();
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [resumeFileName, setResumeFileName] = useState(null);
    /* form state */
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [experiences, setExperiences] = useState([emptyExperience()]);
    const [educations, setEducations] = useState([emptyEducation()]);
    const [skills, setSkills] = useState([]);
    const [skillInput, setSkillInput] = useState('');
    const [openToRoles, setOpenToRoles] = useState([]);
    const [roleInput, setRoleInput] = useState('');
    /* autocomplete */
    const [companySuggestions, setCompanySuggestions] = useState([]);
    const [companySuggestionsLoading, setCompanySuggestionsLoading] = useState(false);
    const [activeCompanyIndex, setActiveCompanyIndex] = useState(null);
    const [collegeSuggestions, setCollegeSuggestions] = useState([]);
    const [collegeSuggestionsLoading, setCollegeSuggestionsLoading] = useState(false);
    const [activeCollegeIndex, setActiveCollegeIndex] = useState(null);
    const clearProfileSetupRequiredFlag = useCallback(() => {
        try {
            localStorage.removeItem(getProfileSetupRequiredKey(user));
        } catch (_) { }
    }, [user]);
    const resolvePostSetupDestination = async () => {
        const routeState = location.state || {};
        const searchedName = routeState.companyName;
        if (!searchedName) {
            return { path: '/dashboard', state: { startTour: true } };
        }
        if (routeState.orgNotFound) {
            return {
                path: '/dashboard',
                state: {
                    ...routeState,
                    startTour: true,
                },
            };
        }
        if (routeState.orgId) {
            return { path: '/insights', state: routeState };
        }
        const results = await searchOrganizations(searchedName);
        if (results && results.length > 0) {
            const matched = results[0];
            return {
                path: '/insights',
                state: {
                    ...routeState,
                    orgId: matched.id,
                    companyName: matched.name,
                },
            };
        }
        return {
            path: '/dashboard',
            state: {
                ...routeState,
                orgNotFound: true,
                startTour: true,
            },
        };
    };
    const finishSetup = async () => {
        setSaving(true);
        try {
            const destination = await resolvePostSetupDestination();
            const destinationState = destination?.state || {};
            const finalCompanyName = destinationState.companyName || location.state?.companyName || '';
            const finalOrgId = destinationState.orgId ?? location.state?.orgId;
            const shouldSkipGoalFocus = destination.path === '/dashboard'
                && (destinationState.orgNotFound || destinationState.insufficientDestinations);
            if (shouldSkipGoalFocus) {
                navigate('/dashboard', { state: destinationState });
                return;
            }
            navigate('/onboarding-goal-focus', {
                state: {
                    nextPath: destination.path,
                    nextState: destinationState,
                    orgId: finalOrgId,
                    companyName: finalCompanyName,
                    role: location.state?.role,
                },
            });
        } catch (err) {
            console.error('Finish setup failed', err);
            navigate('/dashboard', { state: { startTour: true } });
        } finally {
            setSaving(false);
        }
    };
    /* pre-fill from any saved profile */
    const hydrateFromProfile = useCallback(
        (profile) => {
            if (!profile) return;
            setName(profile.name || '');
            setEmail(profile.email || user?.email || '');
            if (profile.work_experiences?.length) {
                setExperiences(
                    profile.work_experiences.map((e) => ({
                        id: e.id,
                        company: e.company || '',
                        role: e.role || '',
                        start_date: toMonthValue(e.start_date),
                        end_date: toMonthValue(e.end_date),
                        currently_working: !e.end_date,
                    })).sort((a, b) => {
                        const scoreA = a.currently_working ? '9999-99' : (a.end_date || a.start_date || '');
                        const scoreB = b.currently_working ? '9999-99' : (b.end_date || b.start_date || '');
                        if (scoreA === scoreB) return (b.start_date || '').localeCompare(a.start_date || '');
                        return scoreB.localeCompare(scoreA);
                    })
                );
            }
            if (profile.educations?.length) {
                setEducations(
                    profile.educations.map((e) => ({
                        id: e.id,
                        college: e.college || '',
                        degree: e.degree || '',
                        start_date: toMonthValue(e.start_date),
                        end_date: toMonthValue(e.end_date),
                        currently_studying: !e.end_date,
                    })).sort((a, b) => {
                        const scoreA = a.currently_studying ? '9999-99' : (a.end_date || a.start_date || '');
                        const scoreB = b.currently_studying ? '9999-99' : (b.end_date || b.start_date || '');
                        if (scoreA === scoreB) return (b.start_date || '').localeCompare(a.start_date || '');
                        return scoreB.localeCompare(scoreA);
                    })
                );
            }
            if (Array.isArray(profile.skills)) {
                setSkills(profile.skills.map(s => typeof s === 'string' ? { skill: s } : { skill: s.skill }));
            }
            if (Array.isArray(profile.open_to_roles)) {
                setOpenToRoles(profile.open_to_roles.map(r => typeof r === 'string' ? r : (r.role || '')));
            }
            setResumeFileName(profile.profile_id || null);
        },
        [user?.email]
    );

    /* auth guard + pre-fill */
    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            openLogin();
            navigate('/');
            return;
        }
        setName(user.name || user.email?.split('@')[0] || '');
        setEmail(user.email || '');
        getProfile()
            .then(hydrateFromProfile)
            .catch(() => { }); // ignore errors; fresh profile is fine
    }, [authLoading, user, openLogin, navigate, hydrateFromProfile]);
    /* org autocomplete debounce */
    useEffect(() => {
        if (activeCompanyIndex == null) {
            setCompanySuggestions([]);
            return;
        }
        const q = (experiences[activeCompanyIndex]?.company || '').trim();
        if (q.length < 2) { setCompanySuggestions([]); return; }
        const t = setTimeout(() => {
            setCompanySuggestionsLoading(true);
            searchOrganizations(q)
                .then((list) => setCompanySuggestions(Array.isArray(list) ? list : []))
                .catch(() => setCompanySuggestions([]))
                .finally(() => setCompanySuggestionsLoading(false));
        }, 220);
        return () => clearTimeout(t);
    }, [activeCompanyIndex, experiences]);
    /* school autocomplete debounce */
    useEffect(() => {
        if (activeCollegeIndex == null) {
            setCollegeSuggestions([]);
            return;
        }
        const q = (educations[activeCollegeIndex]?.college || '').trim();
        if (q.length < 2) { setCollegeSuggestions([]); return; }
        const t = setTimeout(() => {
            setCollegeSuggestionsLoading(true);
            searchSchools(q)
                .then((list) => setCollegeSuggestions(Array.isArray(list) ? list : []))
                .catch(() => setCollegeSuggestions([]))
                .finally(() => setCollegeSuggestionsLoading(false));
        }, 220);
        return () => clearTimeout(t);
    }, [activeCollegeIndex, educations]);
    /* ── navigation ── */
    const skipAll = () => {
        clearProfileSetupRequiredFlag();
        finishSetup();
    };
    const saveAndNext = async () => {
        setSaving(true);
        try {
            const experienceDateError = validateProfileDates(experiences, 'Experience', 'currently_working');
            if (experienceDateError) {
                toast.error(experienceDateError);
                return;
            }
            const educationDateError = validateProfileDates(educations, 'Education', 'currently_studying');
            if (educationDateError) {
                toast.error(educationDateError);
                return;
            }
            const payload = {
                name: name || user?.name || '',
                work_experiences: experiences
                    .map((e) => ({
                        id: e.id,
                        company: e.company || '',
                        role: e.role || '',
                        start_date: e.start_date || '',
                        end_date: e.currently_working ? '' : e.end_date || '',
                    }))
                    .filter((e) => e.company || e.role),
                educations: educations
                    .map((e) => ({
                        id: e.id,
                        college: e.college || '',
                        degree: e.degree || '',
                        start_date: e.start_date || '',
                        end_date: e.currently_studying ? '' : e.end_date || '',
                    }))
                    .filter((e) => e.college || e.degree),
                skills,
                open_to_roles: openToRoles,
            };
            await updateProfile(payload);
        } catch (err) {
            console.error('Profile save error', err);
            // non-blocking — let them continue even if save fails
        } finally {
            setSaving(false);
        }
    };
    const goNext = async () => {
        await saveAndNext();
        if (step < STEPS.length) setStep((s) => s + 1);
        else {
            clearProfileSetupRequiredFlag();
            finishSetup();
        }
    };
    const skipStep = () => {
        if (step < STEPS.length) setStep((s) => s + 1);
        else {
            clearProfileSetupRequiredFlag();
            finishSetup();
        }
    };
    const goBack = () => {
        if (step > 1) setStep((s) => s - 1);
    };
    /* ── resume upload ── */
    const handleResumeUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Please upload a PDF resume');
            return;
        }
        if (file.size > MAX_RESUME_SIZE_BYTES) {
            toast.error('Resume must be 300 KB or smaller');
            e.target.value = '';
            return;
        }
        setUploading(true);
        try {
            const updated = await uploadResume(file);
            hydrateFromProfile(updated);
            toast.success('Resume uploaded and profile auto-filled');
        } catch (err) {
            toast.error(err.message || 'Failed to upload resume');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };
    /* ── experience handlers ── */
    const handleExpChange = (idx, field, val) =>
        setExperiences((prev) =>
            prev.map((exp, i) => {
                if (i !== idx) return exp;
                if (field === 'currently_working') return { ...exp, id: undefined, currently_working: !!val, end_date: val ? '' : exp.end_date };
                if (field === 'end_date') {
                    const nextEnd = clampToCurrentYearEnd(val);
                    if (exp.start_date && nextEnd && nextEnd < exp.start_date) {
                        toast.error('End date cannot be before start date');
                        return exp;
                    }
                    return { ...exp, id: undefined, end_date: nextEnd, currently_working: nextEnd ? false : exp.currently_working };
                }
                if (field === 'start_date') {
                    const nextStart = clampToCurrentYearEnd(val);
                    if (exp.end_date && nextStart && exp.end_date < nextStart) {
                        return { ...exp, id: undefined, start_date: nextStart, end_date: '' };
                    }
                    return { ...exp, id: undefined, start_date: nextStart };
                }
                return { ...exp, id: undefined, [field]: val };
            })
        );
    const addExp = () => setExperiences((p) => [...p, emptyExperience()]);
    const removeExp = (idx) => {
        if (experiences.length === 1) return;
        if (activeCompanyIndex === idx) setActiveCompanyIndex(null);
        else if (activeCompanyIndex != null && activeCompanyIndex > idx) setActiveCompanyIndex(activeCompanyIndex - 1);
        setExperiences((p) => p.filter((_, i) => i !== idx));
    };
    /* ── education handlers ── */
    const handleEduChange = (idx, field, val) =>
        setEducations((prev) =>
            prev.map((edu, i) => {
                if (i !== idx) return edu;
                if (field === 'currently_studying') return { ...edu, id: undefined, currently_studying: !!val, end_date: val ? '' : edu.end_date };
                if (field === 'end_date') {
                    const nextEnd = clampToCurrentYearEnd(val);
                    if (edu.start_date && nextEnd && nextEnd < edu.start_date) {
                        toast.error('End date cannot be before start date');
                        return edu;
                    }
                    return { ...edu, id: undefined, end_date: nextEnd, currently_studying: nextEnd ? false : edu.currently_studying };
                }
                if (field === 'start_date') {
                    const nextStart = clampToCurrentYearEnd(val);
                    if (edu.end_date && nextStart && edu.end_date < nextStart) {
                        return { ...edu, id: undefined, start_date: nextStart, end_date: '' };
                    }
                    return { ...edu, id: undefined, start_date: nextStart };
                }
                return { ...edu, id: undefined, [field]: val };
            })
        );
    const addEdu = () => setEducations((p) => [...p, emptyEducation()]);
    const removeEdu = (idx) => {
        if (educations.length === 1) return;
        if (activeCollegeIndex === idx) setActiveCollegeIndex(null);
        else if (activeCollegeIndex != null && activeCollegeIndex > idx) setActiveCollegeIndex(activeCollegeIndex - 1);
        setEducations((p) => p.filter((_, i) => i !== idx));
    };
    /* ── skill handlers ── */
    const addSkill = () => {
        const v = (skillInput || '').trim();
        if (!v) return;
        if (skills.length >= 30) { toast.error('You can add up to 30 key skills'); return; }
        if (skills.some(s => s.skill.toLowerCase() === v.toLowerCase())) { setSkillInput(''); return; }
        setSkills((p) => [...p, { skill: v }]);
        setSkillInput('');
    };
    const onSkillKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); addSkill(); }
        else if (e.key === 'Backspace' && !skillInput && skills.length) {
            e.preventDefault();
            setSkills((p) => p.slice(0, -1));
        }
    };
    const removeSkill = (skName) => setSkills((p) => p.filter((s) => s.skill !== skName));
    /* ── role handlers ── */
    const addRole = () => {
        const v = (roleInput || '').trim();
        if (!v) return;
        if (openToRoles.some(r => r.toLowerCase() === v.toLowerCase())) { setRoleInput(''); return; }
        if (openToRoles.length >= 7) { toast.error('You can add up to 7 roles'); return; }
        setOpenToRoles((p) => [...p, v]);
        setRoleInput('');
    };
    const onRoleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); addRole(); }
        else if (e.key === 'Backspace' && !roleInput && openToRoles.length) {
            e.preventDefault();
            setOpenToRoles((p) => p.slice(0, -1));
        }
    };
    const removeRole = (rName) => setOpenToRoles((p) => p.filter((r) => r !== rName));
    const { isDark } = useTheme();
    const appCls = isDark ? 'dark-app' : 'light-app';
    const accentColor = isDark ? '#F59E0B' : '#2563eb';
    const accentBg = isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : 'linear-gradient(135deg,#3b82f6,#2563eb)';
    const txtBody = isDark ? 'rgba(255,255,255,0.92)' : '#1e293b';
    const txtSub = isDark ? 'rgba(255,255,255,0.4)' : '#64748b';
    const stepperBg = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
    const stepperBrd = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
    const progressBg = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
    const cardBg = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
    const cardBrd = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
    const skipBtnColor = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
    const skipBtnBrd = isDark ? 'rgba(255,255,255,0.1)' : '#cbd5e1';
    const skipBtnBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(59,130,246,0.04)';
    const btnSolidText = isDark ? '#1a1612' : '#ffffff';
    const dashedBrd = isDark ? 'rgba(245,158,11,0.25)' : 'rgba(37,99,235,0.25)';
    const dashedBg = isDark ? 'rgba(245,158,11,0.04)' : 'rgba(37,99,235,0.04)';
    const dashedHover = isDark ? 'rgba(245,158,11,0.5)' : 'rgba(37,99,235,0.5)';
    const skillTagBg = isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff';
    const skillTagBorder = isDark ? '1px solid rgba(99,102,241,0.2)' : '1px solid #e0e7ff';
    const skillTagText = isDark ? '#818cf8' : '#4f46e5';
    const roleTagBg = isDark ? 'rgba(14,165,233,0.1)' : '#f0f9ff';
    const roleTagBorder = isDark ? '1px solid rgba(14,165,233,0.2)' : '1px solid #e0f2fe';
    const roleTagText = isDark ? '#38bdf8' : '#0284c7';
    if (authLoading) {
        return (
            <div className={`${appCls} flex items-center justify-center`} style={{ minHeight: '100vh' }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
            </div>
        );
    }
    /* ── shared styles (theme-aware) ── */
    const iStyle = isDark
        ? { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.82)' }
        : { background: '#ffffff', border: '1px solid #cbd5e1', color: '#1e293b' };
    const iCls = "w-full h-10 px-3 rounded-lg text-sm focus:outline-none";
    const dropStyle = isDark
        ? { background: '#1a1714', border: '1px solid rgba(255,255,255,0.1)' }
        : { background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 4px 16px rgba(59,130,246,0.1)' };
    const innerCard = isDark
        ? { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px' }
        : { background: '#f8faff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px' };
    const lCls = "block text-xs font-medium mb-1";
    const lStyle = { color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b' };
    const currentStepMeta = STEPS[Math.max(0, step - 1)] || STEPS[0];
    const progressPercent = currentStepMeta?.progress || 0;
    const progressAnimationDuration = step <= 2 ? 0.45 : step === 3 ? 0.65 : 0.8;
    /* ════════════════════════════════════════
       RENDER
    ════════════════════════════════════════ */
    return (
        <div className={`${appCls} flex flex-col`} style={{ minHeight: '100vh' }}>
            {/* ── header ── */}
            <header className="pt-5 px-6 flex items-center justify-between" style={{ background: 'transparent' }}>
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                    <div className="w-8 h-8 flex items-center justify-center bg-white rounded-md p-1">
                        <img src="/assets/logo.png" alt="fairhire logo" className="w-full h-full object-contain" />
                    </div>
                    <span className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: txtBody }}>
                        fairhire
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={skipAll} className="text-sm font-medium flex items-center gap-1.5 px-4 py-2 rounded-full transition-all"
                        style={{ color: skipBtnColor, border: `1px solid ${skipBtnBrd}`, background: skipBtnBg }}
                        onMouseEnter={e => { e.currentTarget.style.color = txtBody; e.currentTarget.style.borderColor = accentColor + '80'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = skipBtnColor; e.currentTarget.style.borderColor = skipBtnBrd; }}
                    >
                        Skip for now <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </header>
            {/* ── hero text ── */}
            <div className="text-center mt-10 mb-6 px-4">
                <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: accentColor, letterSpacing: '0.15em' }}>
                    Complete your profile
                </p>
                <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif", color: txtBody }}>
                    Let&apos;s set up your account
                </h1>
                <p className="text-sm" style={{ color: txtSub }}>This helps us personalize your career transition insights</p>
            </div>
            {/* ── stepper ── */}
            <div className="max-w-2xl mx-auto w-full px-4">
                <div className="mb-8 rounded-2xl p-4" style={{ background: stepperBg, border: `1px solid ${stepperBrd}` }}>
                    <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide" style={{ color: txtSub }}>
                        <span>{currentStepMeta.icon} {currentStepMeta.label}</span>
                        <span style={{ color: accentColor }}>{progressPercent}% complete</span>
                    </div>
                    <div className="mt-3 h-2 w-full rounded-full overflow-hidden" style={{ background: progressBg }}>
                        <motion.div
                            className="h-full rounded-full"
                            style={{ background: accentBg }}
                            animate={{ width: `${progressPercent}%` }}
                            transition={{ duration: progressAnimationDuration, ease: 'easeOut' }}
                        />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {STEPS.map((s) => {
                            const done = s.id < step;
                            const active = s.id === step;
                            return (
                                <span
                                    key={s.id}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                                    style={done
                                        ? { background: `${accentColor}28`, border: `1px solid ${accentColor}66`, color: accentColor }
                                        : active
                                            ? { background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(59,130,246,0.06)', border: `1px solid ${accentColor}59`, color: txtBody }
                                            : { background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(59,130,246,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`, color: txtSub }
                                    }
                                >
                                    {done ? <Check className="w-3.5 h-3.5" /> : s.icon}
                                    {s.label}
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>
            {/* ── card ── */}
            <div className="max-w-2xl mx-auto w-full px-4 pb-16">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 32 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -32 }}
                        transition={{ duration: 0.22, ease: 'easeInOut' }}
                        className="rounded-2xl p-6 sm:p-8"
                        style={{ background: cardBg, border: `1px solid ${cardBrd}`, boxShadow: isDark ? 'none' : '0 4px 24px rgba(59,130,246,0.08)' }}
                    >
                        {/* ──────────── STEP 1: Basic Info ──────────── */}
                        {step === 1 && (
                            <>
                                <h2 className="text-base font-semibold mb-5 flex items-center gap-2" style={{ color: txtBody }}>
                                    👤 Basic Information
                                </h2>
                                <div className="space-y-4 mb-6">
                                    <div>
                                        <label className={lCls} style={lStyle}>Full Name</label>
                                        <input type="text" value={name} onChange={e => setName(e.target.value)}
                                            placeholder="Your full name" className={iCls} style={iStyle}
                                        />
                                    </div>
                                    <div>
                                        <label className={lCls} style={lStyle}>Email</label>
                                        <input type="email" value={email} readOnly
                                            className={iCls + " cursor-not-allowed"}
                                            style={{ ...iStyle, background: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc', color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}
                                        />
                                        <p className="text-xs mt-1" style={{ color: txtSub }}>You can't change your email after login.</p>
                                    </div>
                                    {/* Resume upload */}
                                    <div>
                                        <label className={lCls + " mb-2"} style={lStyle}>
                                            Upload Resume <span style={{ color: txtSub }}>(optional)</span>
                                        </label>
                                        {resumeFileName ? (
                                            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}` }}>
                                                <FileText className="w-4 h-4" style={{ color: accentColor }} />
                                                <span className="text-sm truncate flex-1" style={{ color: txtBody }}>{resumeFileName}</span>
                                                <button type="button" onClick={() => setResumeFileName(null)} className="p-1 rounded transition-colors" style={{ color: txtSub }}>
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className={`flex flex-col items-center justify-center w-full h-28 rounded-xl cursor-pointer transition-all ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
                                                style={{ border: `2px dashed ${dashedBrd}`, background: dashedBg }}
                                                onMouseEnter={e => e.currentTarget.style.borderColor = dashedHover}
                                                onMouseLeave={e => e.currentTarget.style.borderColor = dashedBrd}
                                            >
                                                {uploading ? (
                                                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: accentColor }} />
                                                ) : (
                                                    <>
                                                        <Upload className="w-5 h-5 mb-1.5" style={{ color: accentColor }} />
                                                        <span className="text-sm font-medium" style={{ color: txtBody }}>Upload Resume (PDF)</span>
                                                        <span className="text-xs mt-0.5" style={{ color: txtSub }}>Auto-fills your experience. Works best with LinkedIn exports.</span>
                                                    </>
                                                )}
                                                <input type="file" accept="application/pdf" className="hidden" onChange={handleResumeUpload} disabled={uploading} />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                        {/* ──────────── STEP 2: Experience ──────────── */}
                        {step === 2 && (
                            <>
                                <h2 className="text-base font-semibold mb-5 flex items-center gap-2" style={{ color: txtBody }}>
                                    💼 Work Experience
                                </h2>
                                <p className="text-[11px] -mt-3 mb-4" style={{ color: txtSub }}>
                                    Use exact{' '}
                                    <span style={{ color: accentColor, fontWeight: 700 }}>LinkedIn company names</span>{' '}
                                    for better referral matching.
                                </p>
                                <div className="space-y-4 mb-6">
                                    {experiences.map((exp, idx) => (
                                        <div key={idx} style={innerCard}>
                                            <div className="flex items-center justify-between mb-3">
                                                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: txtSub, letterSpacing: '0.1em' }}>Experience {idx + 1}</p>
                                                {experiences.length > 1 && (
                                                    <button type="button" onClick={() => removeExp(idx)} className="text-xs flex items-center gap-1 transition-colors" style={{ color: txtSub }}
                                                        onMouseEnter={e => e.currentTarget.style.color = '#F87171'}
                                                        onMouseLeave={e => e.currentTarget.style.color = txtSub}
                                                    >
                                                        <X className="w-3 h-3" /> Remove
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <label className={lCls} style={lStyle}>Company Name</label>
                                                    <div className="relative">
                                                        <input type="text" value={exp.company}
                                                            onChange={e => handleExpChange(idx, 'company', e.target.value)}
                                                            onFocus={() => setActiveCompanyIndex(idx)}
                                                            onBlur={() => setTimeout(() => setActiveCompanyIndex(c => c === idx ? null : c), 160)}
                                                            placeholder="e.g. Google"
                                                            className={iCls} style={iStyle}
                                                        />
                                                        {activeCompanyIndex === idx && (companySuggestionsLoading || companySuggestions.length > 0) && (
                                                            <div className="absolute left-0 right-0 top-full mt-1 rounded-xl shadow-xl z-30 max-h-44 overflow-auto" style={dropStyle}>
                                                                {companySuggestionsLoading
                                                                    ? <div className="px-3 py-2 text-xs" style={{ color: txtSub }}>Searching...</div>
                                                                    : companySuggestions.map(org => (
                                                                        <button key={org.id || org.name} type="button"
                                                                            onMouseDown={e => { e.preventDefault(); handleExpChange(idx, 'company', org.name || ''); setActiveCompanyIndex(null); }}
                                                                            className="w-full text-left px-3 py-2 text-sm transition-colors"
                                                                            style={{ color: txtBody }}
                                                                            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(59,130,246,0.06)'}
                                                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                        >{org.name}</button>
                                                                    ))
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className={lCls} style={lStyle}>Role / Title</label>
                                                    <input type="text" value={exp.role}
                                                        onChange={e => handleExpChange(idx, 'role', e.target.value)}
                                                        placeholder="e.g. Product Manager"
                                                        className={iCls} style={iStyle}
                                                    />
                                                </div>
                                                <div>
                                                    <label className={lCls} style={lStyle}>Start Date</label>
                                                    <MonthYearPicker
                                                        value={exp.start_date || ''}
                                                        onChange={(value) => handleExpChange(idx, 'start_date', value)}
                                                        className={iCls}
                                                        style={iStyle}
                                                        maxMonth={CURRENT_YEAR_END_MONTH}
                                                    />
                                                </div>
                                                <div>
                                                    <label className={lCls} style={lStyle}>End Date</label>
                                                    <MonthYearPicker
                                                        value={exp.end_date || ''}
                                                        onChange={(value) => handleExpChange(idx, 'end_date', value)}
                                                        disabled={!!exp.currently_working}
                                                        className={iCls}
                                                        maxMonth={CURRENT_YEAR_END_MONTH}
                                                        style={exp.currently_working ? { ...iStyle, opacity: 0.35, cursor: 'not-allowed' } : iStyle}
                                                    />
                                                    <label className="inline-flex items-center gap-2 text-xs mt-2 cursor-pointer" style={{ color: 'rgba(255,255,255,0.4)' }}>
                                                        <input type="checkbox" checked={!!exp.currently_working}
                                                            onChange={e => handleExpChange(idx, 'currently_working', e.target.checked)}
                                                            className="rounded" style={{ accentColor: accentColor }}
                                                        />
                                                        Currently working here
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={addExp}
                                        className="w-full rounded-xl py-3 text-sm flex items-center justify-center gap-2 transition-all"
                                        style={{ border: `2px dashed ${dashedBrd}`, color: txtSub, background: dashedBg }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = dashedHover; e.currentTarget.style.color = accentColor; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = dashedBrd; e.currentTarget.style.color = txtSub; }}
                                    >
                                        <Plus className="w-4 h-4" /> Add Experience
                                    </button>
                                </div>
                            </>
                        )}
                        {/* ──────────── STEP 3: Education ──────────── */}
                        {step === 3 && (
                            <>
                                <h2 className="text-base font-semibold mb-5 flex items-center gap-2" style={{ color: txtBody }}>
                                    🎓 Education
                                </h2>
                                <p className="text-[11px] -mt-3 mb-4" style={{ color: txtSub }}>
                                    Use exact{' '}
                                    <span style={{ color: accentColor, fontWeight: 700 }}>LinkedIn institution names</span>{' '}
                                    for better referral matching.
                                </p>
                                <div className="space-y-4 mb-6">
                                    {educations.map((edu, idx) => (
                                        <div key={idx} style={innerCard}>
                                            <div className="flex items-center justify-between mb-3">
                                                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: txtSub, letterSpacing: '0.1em' }}>Education {idx + 1}</p>
                                                {educations.length > 1 && (
                                                    <button type="button" onClick={() => removeEdu(idx)} className="text-xs flex items-center gap-1 transition-colors" style={{ color: txtSub }}
                                                        onMouseEnter={e => e.currentTarget.style.color = '#F87171'}
                                                        onMouseLeave={e => e.currentTarget.style.color = txtSub}
                                                    >
                                                        <X className="w-3 h-3" /> Remove
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <label className={lCls} style={lStyle}>College / University</label>
                                                    <div className="relative">
                                                        <input type="text" value={edu.college}
                                                            onChange={e => handleEduChange(idx, 'college', e.target.value)}
                                                            onFocus={() => setActiveCollegeIndex(idx)}
                                                            onBlur={() => setTimeout(() => setActiveCollegeIndex(c => c === idx ? null : c), 160)}
                                                            placeholder="e.g. IIT Bombay"
                                                            className={iCls} style={iStyle}
                                                        />
                                                        {activeCollegeIndex === idx && (collegeSuggestionsLoading || collegeSuggestions.length > 0) && (
                                                            <div className="absolute left-0 right-0 top-full mt-1 rounded-xl shadow-xl z-30 max-h-44 overflow-auto" style={dropStyle}>
                                                                {collegeSuggestionsLoading
                                                                    ? <div className="px-3 py-2 text-xs" style={{ color: txtSub }}>Searching...</div>
                                                                    : collegeSuggestions.map(school => (
                                                                        <button key={school.id || school.name} type="button"
                                                                            onMouseDown={e => { e.preventDefault(); handleEduChange(idx, 'college', school.name || ''); setActiveCollegeIndex(null); }}
                                                                            className="w-full text-left px-3 py-2 text-sm transition-colors"
                                                                            style={{ color: txtBody }}
                                                                            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(59,130,246,0.06)'}
                                                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                        >{school.name}</button>
                                                                    ))
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className={lCls} style={lStyle}>Degree / Department</label>
                                                    <input type="text" value={edu.degree}
                                                        onChange={e => handleEduChange(idx, 'degree', e.target.value)}
                                                        placeholder="e.g. B.Tech Computer Science"
                                                        className={iCls} style={iStyle}
                                                    />
                                                </div>
                                                <div>
                                                    <label className={lCls} style={lStyle}>Start Date</label>
                                                    <MonthYearPicker
                                                        value={edu.start_date || ''}
                                                        onChange={(value) => handleEduChange(idx, 'start_date', value)}
                                                        className={iCls}
                                                        style={iStyle}
                                                        maxMonth={CURRENT_YEAR_END_MONTH}
                                                    />
                                                </div>
                                                <div>
                                                    <label className={lCls} style={lStyle}>End Date</label>
                                                    <MonthYearPicker
                                                        value={edu.end_date || ''}
                                                        onChange={(value) => handleEduChange(idx, 'end_date', value)}
                                                        disabled={!!edu.currently_studying}
                                                        className={iCls}
                                                        maxMonth={CURRENT_YEAR_END_MONTH}
                                                        style={edu.currently_studying ? { ...iStyle, opacity: 0.35, cursor: 'not-allowed' } : iStyle}
                                                    />
                                                    <label className="inline-flex items-center gap-2 text-xs mt-2 cursor-pointer" style={{ color: 'rgba(255,255,255,0.4)' }}>
                                                        <input type="checkbox" checked={!!edu.currently_studying}
                                                            onChange={e => handleEduChange(idx, 'currently_studying', e.target.checked)}
                                                            className="rounded" style={{ accentColor: accentColor }}
                                                        />
                                                        Currently studying
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={addEdu}
                                        className="w-full rounded-xl py-3 text-sm flex items-center justify-center gap-2 transition-all"
                                        style={{ border: `2px dashed ${dashedBrd}`, color: txtSub, background: dashedBg }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = dashedHover; e.currentTarget.style.color = accentColor; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = dashedBrd; e.currentTarget.style.color = txtSub; }}
                                    >
                                        <Plus className="w-4 h-4" /> Add Education
                                    </button>
                                </div>
                            </>
                        )}
                        {/* ──────────── STEP 4: Skills ──────────── */}
                        {step === 4 && (
                            <>
                                <h2 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ color: txtBody }}>
                                    ⚡ Skills
                                </h2>
                                <p className="text-xs mb-5" style={{ color: txtSub }}>
                                    Add skills individually as tags. Press Enter to add. You can add up to 30 skills.
                                </p>
                                <div className="flex gap-2 mb-4">
                                    <input type="text" value={skillInput}
                                        onChange={e => setSkillInput(e.target.value)}
                                        onKeyDown={onSkillKeyDown}
                                        placeholder="Type a skill and press Enter"
                                        className="flex-1 h-11 px-3 rounded-xl text-sm focus:outline-none"
                                        style={iStyle}
                                    />
                                    <button type="button" onClick={addSkill}
                                        className="px-4 h-11 rounded-xl text-sm font-semibold transition-all"
                                        style={{ background: accentBg, color: btnSolidText }}
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2 min-h-[60px] p-3 rounded-xl" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(59,130,246,0.03)', border: `1px solid ${stepperBrd}` }}>
                                    {skills.length === 0 ? (
                                        <span className="text-xs self-center" style={{ color: txtSub }}>No skills added yet. Start typing to add one.</span>
                                    ) : (
                                        skills.map(sk => (
                                            <span key={sk.skill} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                                                style={{ background: skillTagBg, border: skillTagBorder, color: skillTagText }}
                                            >
                                                {sk.skill}
                                                <button type="button" onClick={() => removeSkill(sk.skill)} style={{ color: skillTagText, opacity: 0.6 }}>
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))
                                    )}
                                </div>
                            </>
                        )}
                        {/* ──────────── STEP 5: Open to Roles ──────────── */}
                        {step === 5 && (
                            <>
                                <h2 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ color: txtBody }}>
                                    🎯 Open to Roles
                                </h2>
                                <p className="text-xs mb-5" style={{ color: txtSub }}>
                                    Suggested roles you are open to. You can add up to 7 roles.
                                </p>
                                <div className="flex gap-2 mb-4">
                                    <input type="text" value={roleInput}
                                        onChange={e => setRoleInput(e.target.value)}
                                        onKeyDown={onRoleKeyDown}
                                        placeholder="Type a role and press Enter"
                                        className="flex-1 h-11 px-3 rounded-xl text-sm focus:outline-none"
                                        style={iStyle}
                                    />
                                    <button type="button" onClick={addRole}
                                        className="px-4 h-11 rounded-xl text-sm font-semibold transition-all"
                                        style={{ background: accentBg, color: btnSolidText }}
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2 min-h-[60px] p-3 rounded-xl" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(59,130,246,0.03)', border: `1px solid ${stepperBrd}` }}>
                                    {openToRoles.length === 0 ? (
                                        <span className="text-xs self-center" style={{ color: txtSub }}>No roles added yet.</span>
                                    ) : (
                                        openToRoles.map(role => (
                                            <span key={role} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                                                style={{ background: roleTagBg, border: roleTagBorder, color: roleTagText }}
                                            >
                                                {role}
                                                <button type="button" onClick={() => removeRole(role)} style={{ color: roleTagText, opacity: 0.6 }}>
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))
                                    )}
                                </div>
                            </>
                        )}
                        {/* ── footer controls ── */}
                        <div className="flex items-center justify-between mt-6 pt-5" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}` }}>
                            <button type="button" onClick={goBack} disabled={step === 1}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm transition-all disabled:opacity-30 disabled:pointer-events-none"
                                style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#cbd5e1'}`, color: skipBtnColor }}
                                onMouseEnter={e => { e.currentTarget.style.color = txtBody; e.currentTarget.style.borderColor = accentColor; }}
                                onMouseLeave={e => { e.currentTarget.style.color = skipBtnColor; e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#cbd5e1'; }}
                            >
                                <ArrowLeft className="w-3.5 h-3.5" /> Back
                            </button>
                            <div className="flex items-center gap-3">
                                <button type="button" onClick={skipStep} className="text-sm transition-colors"
                                    style={{ color: skipBtnColor }}
                                    onMouseEnter={e => e.currentTarget.style.color = txtBody}
                                    onMouseLeave={e => e.currentTarget.style.color = skipBtnColor}
                                >
                                    Skip step
                                </button>
                                <button type="button" onClick={goNext} disabled={saving}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60 transition-all shadow-sm"
                                    style={{ background: accentBg, color: btnSolidText }}
                                >
                                    {saving ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : step === STEPS.length ? (
                                        <>Save &amp; View Insights <ArrowRight className="w-3.5 h-3.5" /></>
                                    ) : (
                                        <>Save &amp; Continue <ArrowRight className="w-3.5 h-3.5" /></>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
                <p className="text-center text-xs mt-5" style={{ color: txtSub }}>
                    You can always update your profile later from the account menu.
                </p>
            </div>
        </div>
    );
}
